// Track List - Display and manage all learning blocks
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Edit3,
  Trash2,
  Users,
  BookOpen,
  Calendar,
  DollarSign,
  Award,
  Target,
  AlertCircle,
  GitCommit,
  Eye,
  Image as ImageIcon,
  Box
} from 'lucide-react';
import blocksService from '../../services/blocksService';

// Individual Learning Block List Item Component
const BlockListItem = ({ block, onEdit, onDelete }) => {
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';

    // Parse date string as YYYY-MM-DD
    const dateOnly = dateString.split('T')[0];
    const [year, month, day] = dateOnly.split('-');

    // Create date at noon local time to avoid timezone issues
    const date = new Date(year, month - 1, day, 12, 0, 0);

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-[#B7F1B6] text-[#3C3C3C]';
      case 'draft':
        return 'bg-[#D9D9D9] text-[#3C3C3C]';
      case 'inactive':
        return 'bg-[#FFECC5] text-[#3C3C3C]';
      case 'archived':
        return 'bg-[#FFC6C5] text-[#3C3C3C]';
      default:
        return 'bg-[#D9D9D9] text-[#3C3C3C]';
    }
  };

  const getCompetencyColor = (level) => {
    switch (level) {
      case 'Beginner':
        return 'bg-[#B7F1B6] text-[#3C3C3C]';
      case 'Intermediate':
        return 'bg-[#FFECC5] text-[#3C3C3C]';
      case 'Advanced':
        return 'bg-[#FFC6C5] text-[#3C3C3C]';
      default:
        return 'bg-[#D9D9D9] text-[#3C3C3C]';
    }
  };

  // Calculate total upfront price with rounding
  const calculateUpfrontTotal = () => {
    const baseTotal = parseFloat(block.priceUpfront || 0);
    const materialsFees = block.waiveUpfrontFees ? 0 : parseFloat(block.totalClassFees || 0);

    let discount = 0;
    if (block.upfrontDiscountType === 'percentage') {
      discount = (baseTotal + materialsFees) * (parseFloat(block.upfrontDiscountValue || 0) / 100);
    } else if (block.upfrontDiscountType === 'dollar') {
      discount = parseFloat(block.upfrontDiscountValue || 0);
    }

    let total = baseTotal + materialsFees - discount;

    if (block.roundUpfrontPrice) {
      total = Math.round(total);
    }

    return total;
  };

  // Calculate total monthly price with rounding
  const calculateMonthlyTotal = () => {
    const baseTotal = parseFloat(block.priceMonthly || 0);
    const numberOfMonths = Math.max(block.numberOfMonths || 1, 1);
    const materialsFees = block.waiveMonthlyFees ? 0 : (parseFloat(block.totalClassFees || 0) / numberOfMonths);

    let discount = 0;
    if (block.monthlyDiscountType === 'percentage') {
      discount = (baseTotal + materialsFees) * (parseFloat(block.monthlyDiscountValue || 0) / 100);
    } else if (block.monthlyDiscountType === 'dollar') {
      discount = parseFloat(block.monthlyDiscountValue || 0);
    }

    let total = baseTotal + materialsFees - discount;

    if (block.roundMonthlyPrice) {
      total = Math.round(total);
    }

    return total;
  };

  const upfrontTotal = calculateUpfrontTotal();
  const monthlyTotal = calculateMonthlyTotal();

  return (
    <div
      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => navigate(`/learning-blocks/edit/${block.id}`)}
    >
      <div className="flex items-center p-4 gap-4">
        {/* Track Image */}
        <div className="relative w-16 h-16 flex-shrink-0 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
          {block.imageUrl ? (
            <img
              src={block.imageUrl}
              alt={block.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-700" style={{ display: block.imageUrl ? 'none' : 'flex' }}>
            <GitCommit className="w-6 h-6 text-gray-400 dark:text-gray-500" />
          </div>
        </div>

        {/* Track Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            {/* Left Column - Track Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-base font-semibold text-[#3C3C3C] dark:text-gray-100 truncate">
                  {block.name}
                </h3>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${getStatusColor(block.status)}`}>
                  {block.status.charAt(0).toUpperCase() + block.status.slice(1)}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
                {/* Competency Level */}
                {block.competencyLevel && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium ${getCompetencyColor(block.competencyLevel)}`}>
                    <Award className="w-3 h-3" />
                    {block.competencyLevel}
                  </span>
                )}

                {/* Track Emphasis */}
                {block.trackEmphasis && (
                  <span className="flex items-center gap-1">
                    <Target className="w-3 h-3" />
                    {block.trackEmphasis}
                  </span>
                )}

                {/* Date Range */}
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatDate(block.startDate)} - {formatDate(block.endDate)}
                </span>

                {/* Classes Count */}
                <span className="flex items-center gap-1">
                  <BookOpen className="w-3 h-3" />
                  {block.classCount || 0} {block.classCount === 1 ? 'class' : 'classes'}
                </span>

                {/* Enrollments Count */}
                <span className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {block.enrollmentCount || 0} {block.enrollmentCount === 1 ? 'student' : 'students'}
                </span>
              </div>
            </div>

            {/* Right Column - Pricing & Actions */}
            <div className="flex items-center gap-4 flex-shrink-0">
              {/* Pricing */}
              <div className="text-right">
                <div className="text-xs text-gray-500 dark:text-gray-400">Upfront</div>
                <div className="text-sm font-semibold text-[#3C3C3C] dark:text-gray-100">
                  ${block.roundUpfrontPrice ? upfrontTotal.toFixed(0) : upfrontTotal.toFixed(2)}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Monthly</div>
                <div className="text-sm font-semibold text-[#3C3C3C] dark:text-gray-100">
                  ${block.roundMonthlyPrice ? monthlyTotal.toFixed(0) : monthlyTotal.toFixed(2)}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/learning-blocks/edit/${block.id}`);
                  }}
                  className="p-2 text-gray-400 dark:text-gray-500 hover:text-[#7A99D8] dark:hover:text-[#7A99D8] hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  title="View/Edit Track"
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(block);
                  }}
                  className="p-2 text-gray-400 dark:text-gray-500 hover:text-[#7A99D8] dark:hover:text-[#7A99D8] hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  title="Edit Track"
                >
                  <Edit3 className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(block);
                  }}
                  className="p-2 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                  title="Delete Track"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TrackList = () => {
  const navigate = useNavigate();
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchTracks();
  }, []);

  const fetchTracks = async () => {
    try {
      setLoading(true);
      const data = await blocksService.getAllBlocks();
      setBlocks(data.blocks || []);
    } catch (err) {
      console.error('Error fetching blocks:', err);
      setError('Failed to load blocks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (block) => {
    navigate(`/learning-blocks/edit/${block.id}`);
  };

  const handleDelete = async (blockId) => {
    try {
      await blocksService.deleteBlock(blockId);
      setBlocks(blocks.filter(b => b.id !== blockId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting block:', err);
      alert('Failed to delete block. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Box className="w-8 h-8 text-[#7A99D8]" />
              <div>
                <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-gray-100">Blocks</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">Sequential learning programs for skill development</p>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7A99D8]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Box className="w-8 h-8 text-[#7A99D8]" />
            <div>
              <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-gray-100">Blocks</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">{blocks.length} block{blocks.length !== 1 ? 's' : ''} • Sequential learning programs for skill development</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/learning-blocks/add')}
            className="bg-[#FFECC5] hover:bg-[#FFE4A3] dark:bg-blue-600 dark:hover:bg-blue-700 text-[#3C3C3C] dark:text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Create Block</span>
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Tracks List */}
        {blocks.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
            <div className="max-w-md mx-auto">
              <Target className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">No blocks yet</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Create your first block to organize classes into sequential programs
              </p>
              <button
                onClick={() => navigate('/learning-blocks/add')}
                className="inline-flex items-center gap-2 bg-[#7A99D8] hover:bg-[#6A89C8] text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                <Plus className="w-5 h-5" />
                Create Your First Block
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {blocks.map((block) => (
              <BlockListItem
                key={block.id}
                block={block}
                onEdit={handleEdit}
                onDelete={(block) => setDeleteConfirm(block.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Delete Block?</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete this block? This action cannot be undone and will
              remove all class assignments and enrollments.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackList;
